import * as amplitude from '@amplitude/analytics-browser';
import {TRACK_EVENT_KEYS} from '../constants/amplitude';

const DEFAULT_TRACKING_PROPERTIES = {
    // eslint-disable-next-line camelcase
    page_url: document?.URL ?? '',
};

/**
 * Initialize Amplitude Analytics
 */
const initializeAmplitude = () => {
    if (!amplitude.getSessionId()) {
        const apiKey =
            window?.process?.env?.AMPLITUDE_API_KEY ??
            process.env.AMPLITUDE_API_KEY;

        if (!apiKey) {
            // eslint-disable-next-line no-console
            console.error('Amplitude API key is not configured');

            return;
        }

        const defaultOptions = {
            defaultTracking: {
                sessions: true,
                pageViews: false,
                formInteractions: false,
                fileDownloads: false,
            },
        };

        amplitude.init(apiKey, null, defaultOptions);
    }
};

// No longer using a separate map of display names; we send the key itself as the event name.

/**
 * Function to track events dynamically.
 * @param {string} eventName - The event name value from TRACK_EVENT_KEYS (e.g., 'coaching_page_viewed').
 * @param {Object} props - Additional tracking properties (optional).
 * @returns {Promise} - Resolves immediately after tracking (flush happens asynchronously)
 */
const setTrackingData = (eventName, props = {}) => {
    try {
        initializeAmplitude();

        // Validate that the event name is a known value in TRACK_EVENT_KEYS
        const isValidEvent =
            Object.values(TRACK_EVENT_KEYS).includes(eventName);

        if (!isValidEvent) {
            // eslint-disable-next-line no-console
            console.warn(
                `Tracking event "${eventName}" is not defined in TRACK_EVENT_KEYS.`
            );

            return Promise.resolve();
        }

        const mergedProps = {
            ...DEFAULT_TRACKING_PROPERTIES,
            ...props,
        };

        // Track the event using the provided event name
        const result = amplitude.track(eventName, mergedProps);
        // Flush events asynchronously without blocking (fire-and-forget)
        const flushResult = amplitude.flush();

        // Only add catch if flush returns a promise
        if (flushResult && typeof flushResult.catch === 'function') {
            flushResult.catch(flushError => {
                // eslint-disable-next-line no-console
                console.warn('Amplitude flush failed:', flushError);
            });
        }

        return result;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error tracking event:', error);

        return Promise.resolve();
    }
};

/**
 * Set user ID
 * @param {string} userId - User ID
 */
const setUserId = userId => {
    initializeAmplitude();
    amplitude.setUserId(userId);
};

/**
 * Set user properties
 * @param {Object} properties - User properties object
 */
const setUserProperties = properties => {
    initializeAmplitude();

    const identifyEvent = new amplitude.Identify();

    Object.entries(properties).forEach(([key, value]) => {
        identifyEvent.set(key, value);
    });

    amplitude.identify(identifyEvent);
};

/**
 * Set impersonation user property
 * @param {boolean} isImpersonation - Whether user is being impersonated
 */
const setImpersonationAmplitudeUserProperty = isImpersonation => {
    setUserProperties({impersonation: isImpersonation});
};

/**
 * Identify user with appropriate user_id and properties based on migration status
 * This should be called during session_start to ensure user identification happens early
 * @param {string} livongoUuid - Livongo UUID (alphanumeric)
 * @param {Object} accountData - Account data from getInternalUserId API (optional)
 * @param {string} accountData.uuid - Teladoc member_id (numeric) if migrated
 */
const identifyUser = (livongoUuid, accountData = null) => {
    initializeAmplitude();

    // If user has completed migration to Teladoc Health App
    if (accountData && accountData.uuid) {
        const teladocMemberId = accountData.uuid;

        // Set Teladoc member_id as the primary user_id
        amplitude.setUserId(teladocMemberId);

        // Set both user properties
        setUserProperties({
            // eslint-disable-next-line camelcase
            user_id: teladocMemberId,
            // eslint-disable-next-line camelcase
            livongo_uuid: livongoUuid,
        });
    } else {
        // User has not completed migration (Legacy Livongo experience)
        // Set Livongo UUID as the user_id
        amplitude.setUserId(livongoUuid);

        setUserProperties({
            // eslint-disable-next-line camelcase
            user_id: livongoUuid,
        });
    }
};

/**
 * Track an event directly
 * @param {string} eventName - Name of the event
 * @param {Object} eventProperties - Event properties (optional)
 */
const trackEvent = (eventName, eventProperties = {}) => {
    initializeAmplitude();
    amplitude.track(eventName, {
        ...DEFAULT_TRACKING_PROPERTIES,
        ...eventProperties,
    });
};

/**
 * Reset user (logout)
 */
const resetAmplitude = () => {
    amplitude.reset();
};

/**
 * Get current session ID
 * @returns {number|undefined} Session ID
 */
const getSessionId = () => {
    return amplitude.getSessionId();
};

/**
 * Get current device ID
 * @returns {string|undefined} Device ID
 */
const getDeviceId = () => {
    return amplitude.getDeviceId();
};

const AmpliUtils = {
    setTrackingData,
    setImpersonationAmplitudeUserProperty,
    setUserId,
    setUserProperties,
    identifyUser,
    trackEvent,
    resetAmplitude,
    getSessionId,
    getDeviceId,
};

export default AmpliUtils;
